import { Injectable } from '@angular/core';
import { DATA_RANGE_LIMITS, NUMBERS_CONSTANTS } from '../../common/numbers-constants';
import { SWELLING_MESSAGES } from '../../common/messages-constants';
import { SWELLING_FORM_CONTROL_NAMES_FULL, SWELLING_VALIDATION_FIELD_LABELS } from '../../common/experiment-constants';
import { AdditionTargetGasInputStr, AdditionPressureVolumeInputStr, SwellingFieldValidationResult, SwellingArrayValidationResult } from '../models/swelling';

@Injectable({
  providedIn: 'root'
})
export class SwellingValidatorService {

  private readonly validator = /^[-]?(?:\d+\.\d+|\d+)$/;

  /**
   * Check data range - returns warning message if out of range, null otherwise
   */
  CheckDataRange(min: number, max: number, label: string, rowIndex: number, value: number | string | null): string | null {
    if ((value !== undefined && value !== null && value !== "") &&
      (Number(value) < min || Number(value) > max)) {
      if (rowIndex === NUMBERS_CONSTANTS.zero) {
        return `Input value out of range for: ${label}`;
      } else {
        return `Input value out of range: ${label} for row number: ${rowIndex}`;
      }
    }
    return null;
  }

  /**
   * Check single form field range - returns warning message or null
   */
  CheckInputFieldRange(value: number | string | null, min: number, max: number, label: string): string | null {
    if ((value !== undefined && value !== null && value !== "") && (Number(value) < min || Number(value) > max)) {
      return `Input value out of range: ${label}`;
    }
    return null;
  }

  /**
   * Validate single field value - returns error info if invalid
   */
  ValidateField(value: number | string | null, label: string, rowIndex: number, fieldKey: string): SwellingFieldValidationResult {
    if (value === '' || value === null || value === undefined) {
      return {
        isValid: false,
        errorMessage: `Please enter value for ${label} for row number: ${rowIndex + 1}`,
        fieldId: `${fieldKey}_${rowIndex}`
      };
    } else if (!this.validator.test(String(value))) {
      return {
        isValid: false,
        errorMessage: `Please enter valid value for ${label} for row number: ${rowIndex + 1}`,
        fieldId: `${fieldKey}_${rowIndex}`
      };
    }
    return { isValid: true };
  }

  /**
   * Step 1: Validate Initial Form data ranges
   * Returns warning messages for out-of-range values
   */
  ValidateStep1Ranges(formValues: Record<string, unknown>): string[] {
    const warningMessages: string[] = [];

    const checks: { field: string; min: number; max: number; label: string }[] = [
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.reservoirTemperature, min: DATA_RANGE_LIMITS.reservoirTemperature.min, max: DATA_RANGE_LIMITS.reservoirTemperature.max, label: SWELLING_VALIDATION_FIELD_LABELS.reservoirTemperature },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.reservoirPressure, min: DATA_RANGE_LIMITS.reservoirPressure.min, max: DATA_RANGE_LIMITS.reservoirPressure.max, label: SWELLING_VALIDATION_FIELD_LABELS.reservoirPressure },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.originalVolumePVTCell, min: DATA_RANGE_LIMITS.originalVolumePvtCell.min, max: DATA_RANGE_LIMITS.originalVolumePvtCell.max, label: SWELLING_VALIDATION_FIELD_LABELS.originalVolumePVTCell },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.densityOfFluid, min: DATA_RANGE_LIMITS.densityOfFluid.min, max: DATA_RANGE_LIMITS.densityOfFluid.max, label: SWELLING_VALIDATION_FIELD_LABELS.densityOfFluid },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.molecularWeight, min: DATA_RANGE_LIMITS.molecularWeight.min, max: DATA_RANGE_LIMITS.molecularWeight.max, label: SWELLING_VALIDATION_FIELD_LABELS.molecularWeight },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPResT, min: DATA_RANGE_LIMITS.fluidDensitySatP.min, max: DATA_RANGE_LIMITS.fluidDensitySatP.max, label: SWELLING_VALIDATION_FIELD_LABELS.fluidDensitySatPResT },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPT2, min: DATA_RANGE_LIMITS.fluidDensitySatP.min, max: DATA_RANGE_LIMITS.fluidDensitySatP.max, label: SWELLING_VALIDATION_FIELD_LABELS.fluidDensitySatPT2 },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPT3, min: DATA_RANGE_LIMITS.fluidDensitySatP.min, max: DATA_RANGE_LIMITS.fluidDensitySatP.max, label: SWELLING_VALIDATION_FIELD_LABELS.fluidDensitySatPT3 },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPT4, min: DATA_RANGE_LIMITS.fluidDensitySatP.min, max: DATA_RANGE_LIMITS.fluidDensitySatP.max, label: SWELLING_VALIDATION_FIELD_LABELS.fluidDensitySatPT4 },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPT5, min: DATA_RANGE_LIMITS.fluidDensitySatP.min, max: DATA_RANGE_LIMITS.fluidDensitySatP.max, label: SWELLING_VALIDATION_FIELD_LABELS.fluidDensitySatPT5 },
      { field: SWELLING_FORM_CONTROL_NAMES_FULL.injectionGasPropDensity, min: DATA_RANGE_LIMITS.injectionGasDensity.min, max: DATA_RANGE_LIMITS.injectionGasDensity.max, label: SWELLING_VALIDATION_FIELD_LABELS.injectionGasPropDensity },
    ];

    for (const check of checks) {
      const warning = this.CheckInputFieldRange(
        formValues[check.field] as number | string | null,
        check.min,
        check.max,
        check.label
      );
      if (warning) {
        warningMessages.push(warning);
      }
    }

    return warningMessages;
  }

  /**
   * Step 1: Validate Target Gas Table ranges
   */
  ValidateTargetGasRanges(targetGasData: AdditionTargetGasInputStr[]): string[] {
    const warningMessages: string[] = [];

    for (let i = 0; i < targetGasData.length; i++) {
      const value = targetGasData[i].targetInjGasMol;
      if (value !== null && value !== undefined && value !== '') {
        const warning = this.CheckDataRange(
          DATA_RANGE_LIMITS.targetInjGasMol.min,
          DATA_RANGE_LIMITS.targetInjGasMol.max,
          SWELLING_VALIDATION_FIELD_LABELS.targetInjGasMol,
          i + 1,
          value
        );
        if (warning) warningMessages.push(warning);
      }
    }

    return warningMessages;
  }

  /**
   * Step 2: Validate Composition Components ranges
   */
  ValidateStep2Ranges(data: { targetMolePercent: number | string | null; measuredMolePercent: number | string | null }[]): string[] {
    const warningMessages: string[] = [];

    for (let i = 0; i < data.length; i++) {
      const targetValue = data[i].targetMolePercent;
      const measuredValue = data[i].measuredMolePercent;

      if (targetValue !== null && targetValue !== undefined && targetValue !== '') {
        const warning = this.CheckDataRange(
          DATA_RANGE_LIMITS.molePercent.min,
          DATA_RANGE_LIMITS.molePercent.max,
          SWELLING_VALIDATION_FIELD_LABELS.targetMolePercent,
          i + 1,
          targetValue
        );
        if (warning) warningMessages.push(warning);
      }
      if (measuredValue !== null && measuredValue !== undefined && measuredValue !== '') {
        const warning = this.CheckDataRange(
          DATA_RANGE_LIMITS.molePercent.min,
          DATA_RANGE_LIMITS.molePercent.max,
          SWELLING_VALIDATION_FIELD_LABELS.measuredMolePercent,
          i + 1,
          measuredValue
        );
        if (warning) warningMessages.push(warning);
      }
    }

    return warningMessages;
  }

  /**
   * Step 3: Validate Original Reservoir Fluid ranges
   */
  ValidateStep3Ranges(data: { molePercent: number | string | null }[]): string[] {
    const warningMessages: string[] = [];

    for (let i = 0; i < data.length; i++) {
      const value = data[i].molePercent;

      if (value !== null && value !== undefined && value !== '') {
        const warning = this.CheckDataRange(
          DATA_RANGE_LIMITS.molePercent.min,
          DATA_RANGE_LIMITS.molePercent.max,
          SWELLING_VALIDATION_FIELD_LABELS.molePercent,
          i + 1,
          value
        );
        if (warning) warningMessages.push(warning);
      }
    }

    return warningMessages;
  }

  /**
   * Step 4: Validate Actual Gas + Multi-Temperature PVT ranges
   */
  ValidateStep4Ranges(
    actualGasData: { actualGasAdded: number | string | null }[],
    tempData: { additionTemperature: number | string | null; additions: AdditionPressureVolumeInputStr[] }[]
  ): string[] {
    const warningMessages: string[] = [];

    // Validate Actual Gas Table
    for (let i = 0; i < actualGasData.length; i++) {
      const value = actualGasData[i].actualGasAdded;
      if (value !== null && value !== undefined && value !== '') {
        const warning = this.CheckDataRange(
          DATA_RANGE_LIMITS.actualGasAdded.min,
          DATA_RANGE_LIMITS.actualGasAdded.max,
          SWELLING_VALIDATION_FIELD_LABELS.actualGasAdded,
          i + 1,
          value
        );
        if (warning) warningMessages.push(warning);
      }
    }

    // Validate Multi-Temperature PVT Data
    for (let tempIndex = 0; tempIndex < tempData.length; tempIndex++) {
      const tempValue = tempData[tempIndex].additionTemperature;
      const pvtAdditions = tempData[tempIndex].additions;

      // Temperature field range validation
      if (tempValue !== null && tempValue !== undefined && tempValue !== '') {
        const warning = this.CheckDataRange(
          DATA_RANGE_LIMITS.temperature.min,
          DATA_RANGE_LIMITS.temperature.max,
          `${SWELLING_VALIDATION_FIELD_LABELS.temperature} (${tempIndex + 1})`,
          0,
          tempValue
        );
        if (warning) warningMessages.push(warning);
      }

      // PVT table range validations
      for (let addIndex = 0; addIndex < pvtAdditions.length; addIndex++) {
        const satP = pvtAdditions[addIndex].saturationPressure;
        const volOfCell = pvtAdditions[addIndex].volumeOfCell;

        if (satP !== null && satP !== undefined && satP !== '') {
          const warning = this.CheckDataRange(
            DATA_RANGE_LIMITS.reservoirPressure.min,
            DATA_RANGE_LIMITS.reservoirPressure.max,
            `${SWELLING_VALIDATION_FIELD_LABELS.saturationPressure} (Temp ${tempIndex + 1})`,
            addIndex + 1,
            satP
          );
          if (warning) warningMessages.push(warning);
        }
        if (volOfCell !== null && volOfCell !== undefined && volOfCell !== '') {
          const warning = this.CheckDataRange(
            DATA_RANGE_LIMITS.volumeOfCell.min,
            DATA_RANGE_LIMITS.volumeOfCell.max,
            `${SWELLING_VALIDATION_FIELD_LABELS.volumeOfCell} (Temp ${tempIndex + 1})`,
            addIndex + 1,
            volOfCell
          );
          if (warning) warningMessages.push(warning);
        }
      }
    }

    return warningMessages;
  }

  /**
   * Validate arrays consistency before save (target gas, actual gas, temp blocks must match)
   */
  ValidateArraysBeforeSave(targetGasLength: number, actualGasLength: number, tempBlocks: number, tempAdditionsLengths: number[]): SwellingArrayValidationResult {
    // Check 1: At least one temperature block
    if (tempBlocks === NUMBERS_CONSTANTS.zero) {
      return {
        isValid: false,
        message: SWELLING_MESSAGES.atLeastOneTempBlock
      };
    }

    // Check 2: Target vs Actual Gas arrays match
    if (targetGasLength !== actualGasLength) {
      return {
        isValid: false,
        message: SWELLING_MESSAGES.additionMismatch(targetGasLength, actualGasLength)
      };
    }

    // Check 3: Each temperature block's additions count matches
    for (let i = 0; i < tempBlocks; i++) {
      if (tempAdditionsLengths[i] !== targetGasLength) {
        return {
          isValid: false,
          message: SWELLING_MESSAGES.additionTempBlock(i, tempAdditionsLengths[i], targetGasLength)
        };
      }
    }

    return { isValid: true, message: '' };
  }

  /**
   * Get the list of required controls for Step 1 based on temperature block count
   */
  GetStep1RequiredControls(tempCount: number): string[] {
    const requiredControls: string[] = [
      SWELLING_FORM_CONTROL_NAMES_FULL.reservoirTemperature,
      SWELLING_FORM_CONTROL_NAMES_FULL.reservoirPressure,
      SWELLING_FORM_CONTROL_NAMES_FULL.originalVolumePVTCell,
      SWELLING_FORM_CONTROL_NAMES_FULL.originalVolumePVTCellPressure,
      SWELLING_FORM_CONTROL_NAMES_FULL.densityOfFluid,
      SWELLING_FORM_CONTROL_NAMES_FULL.densityOfFluidPressure,
      SWELLING_FORM_CONTROL_NAMES_FULL.molecularWeight,
      SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPResT,
      SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPT2,
      SWELLING_FORM_CONTROL_NAMES_FULL.injectionGasPropDensity
    ];

    if (tempCount >= NUMBERS_CONSTANTS.three) requiredControls.push(SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPT3);
    if (tempCount >= NUMBERS_CONSTANTS.four) requiredControls.push(SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPT4);
    if (tempCount >= NUMBERS_CONSTANTS.five) requiredControls.push(SWELLING_FORM_CONTROL_NAMES_FULL.fluidDensitySatPT5);

    return requiredControls;
  }
}
